<template>
  <div class="page">
    <div class="hero">
      <div class="title">
        <h2>Roster Imports</h2>
        <p class="sub">Upload spreadsheets and import graduates per ceremony.</p>
      </div>
      <div class="uni-select">
        <label>University</label>
        <select v-model="selectedUniversityId">
          <option disabled value="">Select a university…</option>
          <option v-for="u in universities" :key="u.id" :value="u.id">{{ u.name }} ({{ u.slug }})</option>
        </select>
        <span v-if="loadingUniversities" class="muted">Loading…</span>
      </div>
      <div v-if="canWriteImports" class="actions">
        <button class="btn primary" @click="newImport" :disabled="!selectedUniversityId">New Import</button>
      </div>
    </div>

    <div v-if="!selectedUniversityId" class="empty-state">
      <div class="empty-card">
        <div class="text">Select a university to view and create imports.</div>
      </div>
    </div>

    <div v-else>
      <div class="summary">
        <div class="chip">Total: <strong>{{ filteredImports.length }}</strong></div>
        <div class="chip">Validated: <strong>{{ countByStatus('validated') }}</strong></div>
        <div class="chip">Staged: <strong>{{ countByStatus('staged') }}</strong></div>
      </div>
      <div v-if="loading" class="muted">Loading…</div>
      <div v-else>
        <table class="table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Status</th>
              <th>Ceremony</th>
              <th>File</th>
              <th>Created</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="imp in filteredImports" :key="imp.import_id || imp.id">
              <td>{{ imp.import_id || imp.id }}</td>
              <td>
                <span class="pill" :class="statusClass(imp)">{{ statusText(imp) }}</span>
              </td>
              <td>{{ ceremonyLabel(imp) }}</td>
              <td>
                <div class="file-cell">
                  <div>{{ imp.filename || imp.file_name }}</div>
                  <button class="link-btn" @click="downloadOriginal(imp)" :disabled="downloadingImportId === (imp.import_id || imp.id)">
                    {{ downloadingImportId === (imp.import_id || imp.id) ? 'Preparing…' : 'Download original' }}
                  </button>
                </div>
              </td>
              <td>{{ (imp.created_at || imp.createdAt || '').toString().slice(0,19).replace('T',' ') }}</td>
              <td>
                <button class="btn small" @click="open(imp)">Open</button>
              </td>
            </tr>
            <tr v-if="!filteredImports.length">
              <td colspan="5" class="muted">No imports yet for this university.</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { getAccessContext, hasToolAccess } from '../../../admin/access.js'

export default {
  name: 'ImportsList',
  created() {
    this.fetch()
    this.loadUniversities()
  },
  data() { return { loading: false, imports: [], universities: [], selectedUniversityId: '', loadingUniversities: false, downloadingImportId: '' } },
  computed: {
    canWriteImports(){
      return hasToolAccess('importsWrite', getAccessContext())
    },
    filteredImports(){
      if (!this.selectedUniversityId) return []
      return (this.imports || []).filter(i => i.university_id === this.selectedUniversityId)
    }
  },
  methods: {
    fmt(d){
      if (!d) return ''
      const raw = String(d)
      const normalized = raw.includes('T') ? raw : raw.replace(' ', 'T')
      const parsed = new Date(normalized)
      if (!Number.isNaN(parsed.getTime())) return parsed.toLocaleString()
      return raw
    },
    ceremonyLabel(imp){
      return this.fmt(imp.ceremony_datetime) || imp.ceremony_id || '—'
    },
    async fetch() {
      this.loading = true
      try {
        const base = process.env.VUE_APP_BASE_URL
        const r = await axios.get(`${base}/imports`)
        this.imports = (r.data.imports || [])
      } catch (e) { console.error(e) }
      finally { this.loading = false }
    },
    async loadUniversities(){
      this.loadingUniversities = true
      try {
        const base = process.env.VUE_APP_BASE_URL
        const r = await axios.get(`${base}/universities`)
        this.universities = r.data || []
        if (this.universities.length === 1) this.selectedUniversityId = this.universities[0].id
      } catch(e){ console.error(e) }
      finally { this.loadingUniversities = false }
    },
    newImport(){
      if (!this.canWriteImports) return
      if (!this.selectedUniversityId) return
      this.$router.push({ path: '/admin/imports/new', query: { university_id: this.selectedUniversityId } })
    },
    open(imp){
      const id = imp.import_id || imp.id
      if (imp.has_outputs) {
        this.$router.push({ path: `/admin/imports/${id}`, query: { tab: 'run' } })
      } else {
        this.$router.push(`/admin/imports/${id}`)
      }
    },
    async downloadOriginal(imp){
      const id = imp.import_id || imp.id
      if (!id) return
      this.downloadingImportId = id
      try {
        const base = process.env.VUE_APP_BASE_URL
        const r = await axios.get(`${base}/imports/${id}/download`)
        const raw = r.data && r.data.raw
        if (raw && raw.url) {
          window.open(raw.url, '_blank')
        } else {
          window.alert('Original file not available for this import.')
        }
      } catch (e) {
        console.error(e)
        window.alert('Unable to download the original file.')
      } finally {
        if (this.downloadingImportId === id) this.downloadingImportId = ''
      }
    },
    statusText(imp){
      if (imp && imp.has_outputs) return 'Complete'
      const s = imp ? imp.status : ''
      const map = { awaiting_upload: 'Awaiting Upload', mapping_provided: 'Mapping Provided', validated: 'Validated', validation_failed: 'Validation Failed', staged: 'Staged' }
      return map[s] || s
    },
    statusClass(imp){
      const s = imp ? imp.status : ''
      const complete = !!(imp && imp.has_outputs)
      return {
        complete,
        ok: !complete && (s === 'validated' || s === 'staged'),
        warn: s === 'mapping_provided',
        err: s === 'validation_failed',
        idle: s === 'awaiting_upload' || !s
      }
    },
    countByStatus(s){
      return this.filteredImports.filter(i => i.status === s).length
    }
  }
}
</script>

<style scoped>
.page { max-width: 1100px; margin: 0 auto; padding: 16px 18px; }
.hero { display: grid; grid-template-columns: 1fr 320px auto; gap: 16px; align-items: end; margin-bottom: 16px; }
.title h2 { margin: 0; }
.title .sub { margin: 4px 0 0; color: #6b7280; }
.actions { display: flex; justify-content: flex-end; }
.uni-select { display: flex; flex-direction: column; gap: 6px; }
.uni-select select { padding: 8px 10px; border: 1px solid #e5e7eb; border-radius: 6px; background: #fff; }
.uni-select select:focus { outline: none; border-color: #c7d2fe; box-shadow: 0 0 0 3px rgba(199,210,254,.45); }
.muted { color: #6b7280; }
.btn { padding: 8px 12px; border: 1px solid #e5e7eb; background: #fff; border-radius: 6px; cursor: pointer; }
.btn.primary { background: #111827; color: #fff; border-color: #111827; }
.table { width: 100%; border-collapse: collapse; margin-top: 6px; }
.table th, .table td { border-bottom: 1px solid #f3f4f6; padding: 8px; text-align: left; }
.file-cell { display: flex; flex-direction: column; gap: 4px; }
.link-btn { background: transparent; border: none; color: #1d4ed8; padding: 0; cursor: pointer; font-size: 12px; text-align: left; }
.link-btn:disabled { color: #9ca3af; cursor: default; }
.pill { display: inline-block; padding: 2px 8px; border-radius: 9999px; font-size: 12px; font-weight: 600; border: 1px solid transparent; }
.pill.ok { background: #ecfdf5; color: #065f46; border-color: #a7f3d0; }
.pill.complete { background: #ecfeff; color: #0e7490; border-color: #a5f3fc; }
.pill.warn { background: #eff6ff; color: #1e40af; border-color: #bfdbfe; }
.pill.err { background: #fef2f2; color: #991b1b; border-color: #fecaca; }
.pill.idle { background: #f3f4f6; color: #374151; border-color: #e5e7eb; }
.summary { display: flex; gap: 10px; margin: 10px 0 12px; }
.chip { background: #f3f4f6; border-radius: 9999px; padding: 4px 10px; font-size: 12px; }
.empty-state { display: flex; justify-content: center; margin-top: 40px; }
.empty-card { border: 1px solid #e5e7eb; border-radius: 10px; padding: 24px; text-align: center; max-width: 520px; background: #fff; }
</style>
